import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, X, AlertTriangle, ChevronRight } from "lucide-react";
import { useAnnouncements } from "../../../hooks/useAnnouncements";
import AnnouncementModal from "./AnnouncementModal";
import { Pill } from "../../ui";

const PRIORITY_CFG = {
  normal:    { pillBg: "#F1F5F9", pillCol: "#475569", dot: "bg-slate-400" },
  important: { pillBg: "#FEF0E6", pillCol: "#E8690A", dot: "bg-brand-400" },
  urgent:    { pillBg: "#FDECEA", pillCol: "#C0392B", dot: "bg-accent-red" },
};

function relativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function AnnouncementBell({ user }) {
  const { announcements, unreadCount, pendingAck, acknowledge, markRead } = useAnnouncements(user);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeId, setActiveId]         = useState(null);
  const dropRef    = useRef(null);
  const shownRef   = useRef(new Set());

  // Auto-popup first unacknowledged requiresAck item
  useEffect(() => {
    const first = pendingAck[0];
    if (first && !shownRef.current.has(first._id) && !activeId) {
      shownRef.current.add(first._id);
      setActiveId(first._id);
    }
  }, [pendingAck, activeId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openAnnouncement = useCallback((id) => {
    setActiveId(id);
    setDropdownOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    const ann = announcements.find(a => a._id === activeId);
    if (ann) markRead(ann._id);
    setActiveId(null);
  }, [activeId, announcements, markRead]);

  const handleAcknowledge = useCallback(() => {
    if (activeId) acknowledge(activeId);
    setActiveId(null);
  }, [activeId, acknowledge]);

  const activeAnnouncement = announcements.find(a => a._id === activeId) || null;

  // Show 8 most recent
  const displayList = [...announcements]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 8);

  return (
    <>
      {/* Bell button */}
      <div className="relative" ref={dropRef}>
        <button
          onClick={() => setDropdownOpen(p => !p)}
          className="relative w-9 h-9 rounded-lg flex items-center justify-center text-slate-500
                     transition-colors duration-200 hover:bg-slate-100 hover:text-slate-700"
          aria-label="Announcements"
        >
          <Megaphone size={17} strokeWidth={2.5} />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-0.5 bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown */}
        <AnimatePresence>
          {dropdownOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.97 }}
              transition={{ duration: 0.18, ease: "easeOut" }}
              className="absolute right-0 top-11 w-[340px] bg-white rounded-2xl shadow-2xl border border-navy-100 overflow-hidden z-[900]"
            >
              {/* Dropdown header */}
              <div className="px-5 py-3.5 border-b border-navy-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Megaphone size={15} strokeWidth={2.5} className="text-navy-500" />
                  <span className="font-sora font-bold text-[14px] text-navy-800">Announcements</span>
                  {unreadCount > 0 && (
                    <Pill bg="#FDECEA" col="#C0392B" className="!px-2 !py-0.5 !text-[11px]">
                      {unreadCount} new
                    </Pill>
                  )}
                </div>
                <button
                  onClick={() => setDropdownOpen(false)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-navy-400 hover:bg-navy-50 hover:text-navy-800 transition-colors"
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>

              {/* List */}
              <div className="max-h-[400px] overflow-y-auto">
                {displayList.length === 0 && (
                  <div className="py-10 text-center text-navy-400 text-sm">
                    No announcements
                  </div>
                )}
                {displayList.map(ann => {
                  const cfg = PRIORITY_CFG[ann.priority] || PRIORITY_CFG.normal;
                  const isUnread = !ann.readBy?.includes(user?._id);
                  return (
                    <button
                      key={ann._id}
                      onClick={() => openAnnouncement(ann._id)}
                      className="w-full text-left px-5 py-4 border-b border-navy-50 hover:bg-slate-50 transition-colors flex items-start gap-3 group last:border-b-0"
                    >
                      <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${isUnread ? cfg.dot : "bg-navy-200"}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className={`text-[13px] font-semibold truncate ${isUnread ? "text-navy-800" : "text-navy-500"}`}>
                            {ann.title}
                          </div>
                          {ann.requiresAck && !ann.acknowledgedBy?.includes(user?._id) && (
                            <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" strokeWidth={2.5} />
                          )}
                        </div>
                        <div className="text-[12px] text-navy-400 mt-0.5 flex items-center gap-2">
                          <Pill bg={cfg.pillBg} col={cfg.pillCol} className="!px-2 !py-0.5 !text-[10px]">
                            {ann.priority || "normal"}
                          </Pill>
                          <span>{relativeTime(ann.createdAt)}</span>
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-navy-300 group-hover:text-navy-500 transition-colors mt-1 shrink-0" strokeWidth={2.5} />
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Announcement detail modal */}
      {activeAnnouncement && (
        <AnnouncementModal
          announcement={activeAnnouncement}
          onClose={handleClose}
          onAcknowledge={handleAcknowledge}
        />
      )}
    </>
  );
}
